import { h } from 'preact';
import * as BaseComp from 'components/shared/gmap/gmap';
import Store from 'store/store';
import * as actions from 'store/actions';
import { joinClasses, accessNested } from 'utils/utils';
import { track } from 'utils/tracking';
import { getCopy } from 'utils/copy';
import CustomMarker from 'components/shared/maptiler/customMarker.js';
import { SvgCompass } from 'components/shared/svgIcons/svgIcons';
import { Link } from 'components/core/link/link.js';
import s from 'components/shared/maptiler/maptiler.sss';

const loadScript = url => new Promise((res, rej) => {
	let script = document.createElement('script');
	script.onload = res;
	script.onerror = rej;
	script.src = url;
	document.head.appendChild(script);
});
const loadStyle = url => new Promise((res, rej) => {
	let link = document.createElement('link');
	link.setAttribute('rel', 'stylesheet');
	link.onload = res;
	link.onerror = rej;
	link.href = url;
	document.head.appendChild(link);
});
// const promiseSequence = (arr, fn) => arr.reduce((p, e) => p.then(() => fn(e)), Promise.resolve());

// Basic: only accepts px values for font-size (line-height accepts px, em, % or unitless)
function parseFont(str) {
	const tokens = str.match(/('(?:\\'|[^'])+'|"(?:\\"|[^"])+"|[^\s'"/,]+)+|\//g);
	if (!tokens) {
		return null;
	}
	let font = {
		size: 16,
		family: ['sans-serif'],
		lineHeight: 1.2,
	};
	let pxLength = /^0|(\d+|\d*\.\d+)(e\d+)?px$/i;
	let fontSizeIndex = tokens.findIndex(e => pxLength.test(e));
	if (fontSizeIndex === -1) {
		return null;
	}
	font.size = parseInt(tokens[fontSizeIndex]);
	if (tokens[fontSizeIndex + 1] === '/') {
		let lineHeightParts = tokens[fontSizeIndex + 2].match(/^((?:\d+|\d*\.\d+)(?:e\d+)?)(px|em|%)?$/i);
		if (!lineHeightParts) {
			return null;
		}
		switch (lineHeightParts[2]) {
			case '%': font.lineHeight = lineHeightParts[1] / 100; break;
			case 'px': font.lineHeight = lineHeightParts[1] / font.size; break;
			default: font.lineHeight = +lineHeightParts[1];
		}
		fontSizeIndex += 2;
	}
	font.family = tokens.slice(fontSizeIndex + 1).map(e => e[0] === '"' || e[0] === '\'' ? e.slice(1, -1) : e);
	if (!font.family) {
		return null;
	}
	return font;
}

export default class Gmap extends BaseComp.default {

	constructor(props) {
		super(props);
		this.CustomMarkerComp = CustomMarker;
	}

	async initGMaps(e) {
		if (typeof window === 'undefined' || window.L || this.requested) {
			return;
		}
		this.requested = true;
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}
		await Promise.all(
			[
				loadStyle('https://cdn.maptiler.com/mapbox-gl-js/v1.11.1/mapbox-gl.css'),
				loadScript('https://cdn.maptiler.com/mapbox-gl-js/v1.11.1/mapbox-gl.js')
			]
		);
		const maptilerMapId = accessNested(this.props.appData, 'apis.mapTiler.mapId');
		const maptilerKey = accessNested(this.props.appData, 'apis.mapTiler.key');

		this.gmap = new window.mapboxgl.Map({
			container: this.$gmap,
			style: `https://api.maptiler.com/maps/${maptilerMapId}/style.json?key=${maptilerKey}`,
			center: [this.props.center.lon, this.props.center.lat],
			zoom: (this.props.zoom || 12) - 1.4,
		});
		this.gmap.addControl(new window.mapboxgl.NavigationControl(), 'bottom-left');

		this.gmap.on('load', () => {
			this.setState({ loaded: true });

			// Prepare city layers (the data source will be updated in updateCities)
			this.gmap.addSource('cities', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }});
			const cityStyle = this.getCityStyle();
			this.gmap.addLayer({
				id: 'cities',
				source: 'cities',
				type: 'circle',
				paint: {
					'circle-color': cityStyle.colorBack || '#000',
					'circle-opacity': cityStyle.opacityBack === null || typeof cityStyle.opacityBack === 'undefined' ? 1 : cityStyle.opacityBack,
					'circle-radius': (cityStyle.size || 32) / 2
				}
			});
			// Parse font
			let font = parseFont(cityStyle.font || '16px sans-serif');
			this.gmap.addLayer({
				id: 'cities-text',
				source: 'cities',
				type: 'symbol',
				layout: {
					// 'text-field': '{count}',
					'text-field': '+',
					'text-font': font.family,
					'text-size': font.size,
					'text-line-height': font.lineHeight,
				},
				paint: {
					'text-color': cityStyle.colorFront || '#fff',
					'text-opacity': cityStyle.opacityFront === null || typeof cityStyle.opacityFront === 'undefined' ? 1 : cityStyle.opacityFront
				}
			});
			this.gmap.on('mouseenter', 'cities', () => this.gmap.getCanvas().style.cursor = 'pointer');
			this.gmap.on('mouseleave', 'cities', () => this.gmap.getCanvas().style.cursor = '');
			this.gmap.on('click', 'cities', (e) => {
				let clicked = this.gmap.queryRenderedFeatures(e.point, { layers: ['cities'] });
				let city = clicked && clicked[0] && this.renderedCities && this.renderedCities[clicked[0].id];
				if (!city) {
					return;
				}
				track({ a: 'map-city-click', l: city.name });
				Store.emit(actions.SET_CLIENT_GEO, city.geo);
			});

			if (this.props.theaters) {
				this.updateTheaters(this.props.theaters, this.props.useCustomMarker);
			}
			if (this.props.cities) {
				this.updateCities(this.props.cities);
			}
		});
	}

	mountGMaps() {}

	getCoordinatesForMap(geo) {
		return [geo.lon, geo.lat];
	}

	panTo(geo) {
		// animation is broken for some reason (stops very quickly, before reaching the goal)
		this.gmap.panTo(this.getCoordinatesForMap(geo), { animate: false });
	}

	updateTheaters(...args) {
		super.updateTheaters(...args);
		if (this.gmap) {
			// Markers won't appear (get their positioning style assigned) until the next move / zoom
			// Force it
			setTimeout(() => this.gmap.setZoom(this.gmap.getZoom()), 0);
		}
	}

	updateCities(cities) {
		if (!this.gmap) {
			return;
		}

		const searchRadiusFactor = +accessNested(this.props.appData, 'pages.showtimes.data.radiusFactor') || 1;
		let searchRadius = searchRadiusFactor * 10;

		if (!cities || !this.state.loaded) {
			return;
		}

		const renderedCities = cities.filter(city => city.distanceKm >= searchRadius);
		let geojsonFeatures = renderedCities.map((city, i) => {
			return {
				id: i,
				type: 'Feature',
				properties: { count: city.screeningsCount },
				geometry: { type: 'Point', coordinates: [city.geo.lon, city.geo.lat] }
			};
		});
		try {
			this.gmap.getSource('cities').setData({ type: 'FeatureCollection', features: geojsonFeatures });
			this.renderedCities = renderedCities;
		} catch(e) {}
	}

	renderDirections() {
		const props = this.props;
		const state = this.state;

		if (!props.useDirections) {
			return;
		}

		let queryString = ['api=1', 'travelmode=driving'];
		let start = this.props.center;
		if (start) {
			queryString.push('origin=' + start.lat + ',' + start.lon);
		}
		let dest = this.props.activeTheater;
		if (dest) {
			queryString.push('destination=' + dest.geo.lat + ',' + dest.geo.lon);
		}
		let link = 'https://www.google.com/maps/dir/?' + queryString.join('&');

		// DRIVING
		return (
			<div class={joinClasses(s.directionControls, props.activeTheater && s.show)} key="directions">
				<Link href={link} target="_blank" class={s.requestDirections} trackId="maps_directions_clicked">
					<SvgCompass class={s.directionIcon} />
					<span class={s.cta}>{getCopy('REQUEST_DIRECTIONS')}</span>
				</Link>
			</div>
		);
	}
}
